import React, { useEffect, useState } from 'react';
import { api, mutate } from '../shared/api';

/**
 * Admin šifrarnika `bi_delivery_methods` — načini isporuke + max iznos po pošiljci.
 *
 * Koristi se iz m.40.3 (`nacin_isporuke` dropdown na anotaciji) i m.40.4 (bundling preskladištenja
 * po max_amount_eur). Code je read-only nakon kreiranja (history u bi_offer_annotations.value).
 *
 * Manager+/owner edit; ostali read-only.
 */

type DeliveryMethod = {
    id: number;
    code: string;
    name_hr: string;
    name_en: string | null;
    max_amount_eur: number | string | null;
    is_active: boolean | number;
    sort_order: number;
    note: string | null;
    created_at: string | null;
    updated_at: string | null;
};

type Locale = 'hr' | 'en';

const eur = (n: number | string | null | undefined) => {
    if (n == null || n === '') return '∞';
    const x = Number(n);
    if (!isFinite(x)) return '∞';
    return new Intl.NumberFormat('hr-HR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(x);
};

export function DeliveryMethodsAdmin({ locale, token, canEdit }: { locale: Locale; token: string; canEdit: boolean }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [rows, setRows] = useState<DeliveryMethod[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [msg, setMsg] = useState('');
    const [draft, setDraft] = useState<Partial<DeliveryMethod> | null>(null);
    const [editId, setEditId] = useState<number | null>(null);

    const reload = async () => {
        setLoading(true); setError('');
        try {
            const r = await api<{ rows: DeliveryMethod[] }>('/delivery-methods', token);
            setRows(r.rows || []);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally { setLoading(false); }
    };
    useEffect(() => { void reload(); }, [token]);

    const save = async () => {
        if (!draft) return;
        try {
            if (editId) {
                await mutate(`/delivery-methods/${editId}`, token, 'PATCH', {
                    name_hr: draft.name_hr,
                    name_en: draft.name_en || null,
                    max_amount_eur: draft.max_amount_eur === '' || draft.max_amount_eur == null ? null : Number(draft.max_amount_eur),
                    is_active: draft.is_active === true || draft.is_active === 1,
                    sort_order: Number(draft.sort_order ?? 0),
                    note: draft.note || null,
                });
            } else {
                await mutate('/delivery-methods', token, 'POST', {
                    code: (draft.code ?? '').toLowerCase().replace(/[^a-z0-9_]/g, ''),
                    name_hr: draft.name_hr,
                    name_en: draft.name_en || null,
                    max_amount_eur: draft.max_amount_eur === '' || draft.max_amount_eur == null ? null : Number(draft.max_amount_eur),
                    is_active: draft.is_active !== false,
                    sort_order: Number(draft.sort_order ?? 999),
                    note: draft.note || null,
                });
            }
            setDraft(null); setEditId(null); setMsg(t('Spremljeno ✓', 'Saved ✓'));
            setTimeout(() => setMsg(''), 2500);
            await reload();
        } catch (e) { setError(e instanceof Error ? e.message : 'API'); }
    };

    const del = async (id: number, code: string) => {
        if (!confirm(t(`Obrisati '${code}'?`, `Delete '${code}'?`))) return;
        try {
            const r = await mutate<{ ok: boolean; soft?: boolean; hard?: boolean }>(`/delivery-methods/${id}`, token, 'DELETE');
            setMsg(r.soft ? t('Postavljeno neaktivno (već se koristi)', 'Set inactive (already in use)') : t('Obrisano', 'Deleted'));
            setTimeout(() => setMsg(''), 2500);
            await reload();
        } catch (e) { setError(e instanceof Error ? e.message : 'API'); }
    };

    const startEdit = (r: DeliveryMethod) => {
        setEditId(r.id);
        setDraft({ ...r, is_active: !!r.is_active });
    };
    const startNew = () => {
        setEditId(null);
        setDraft({ code: '', name_hr: '', name_en: '', max_amount_eur: null, is_active: true, sort_order: rows.length * 10 + 10, note: '' });
    };

    return (
        <div style={{ padding: 12, background: '#fffdf6', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <h3 style={{ margin: 0, color: 'var(--ad-ink)', fontSize: '1rem' }}>🚚 {t('Načini isporuke (šifrarnik)', 'Delivery methods (codebook)')}</h3>
                <span style={{ fontSize: '0.7rem', color: '#8a8a8a' }}>
                    {t('Koristi se u m.40.3 anotacijama i m.40.4 bundlingu preskladištenja po max iznosu.', 'Used in m.40.3 annotations and m.40.4 transfer bundling by max amount.')}
                </span>
                {canEdit ? (
                    <button type="button" onClick={startNew} style={{ marginLeft: 'auto', background: '#1f7a36', color: '#fff', border: 0, borderRadius: 5, padding: '5px 12px', cursor: 'pointer', fontSize: '0.82rem' }}>+ {t('Novi', 'New')}</button>
                ) : null}
            </div>
            {error ? <div style={{ color: '#b14040', fontSize: '0.82rem', marginBottom: 8 }}>{error}</div> : null}
            {msg ? <div style={{ color: '#1f7a36', fontSize: '0.82rem', marginBottom: 8 }}>{msg}</div> : null}
            {loading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                    <tr style={{ borderBottom: '2px solid var(--ad-acc)', background: 'rgba(var(--ad-acc-rgb),0.08)' }}>
                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>{t('Šifra', 'Code')}</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>{t('Naziv HR', 'Name HR')}</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>{t('Naziv EN', 'Name EN')}</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>{t('Max € / pošiljka', 'Max € / shipment')}</th>
                        <th style={{ padding: '6px 8px', textAlign: 'center' }}>{t('Aktivno', 'Active')}</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>#</th>
                        {canEdit ? <th style={{ padding: '6px 8px' }}></th> : null}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id} style={{ borderBottom: '1px solid #eee5d0', opacity: r.is_active ? 1 : 0.4 }}>
                            <td style={{ padding: '5px 8px', fontFamily: 'ui-monospace, monospace' }}>{r.code}</td>
                            <td style={{ padding: '5px 8px' }}>{r.name_hr}</td>
                            <td style={{ padding: '5px 8px', color: '#8a8a8a' }}>{r.name_en ?? '—'}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: 'ui-monospace, monospace', fontWeight: 700, color: r.max_amount_eur == null ? '#8a8a8a' : 'var(--ad-ink)' }}>
                                {eur(r.max_amount_eur)}
                            </td>
                            <td style={{ padding: '5px 8px', textAlign: 'center' }}>{r.is_active ? '✓' : '—'}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', color: '#8a8a8a' }}>{r.sort_order}</td>
                            {canEdit ? (
                                <td style={{ padding: '5px 8px', textAlign: 'right' }}>
                                    <button type="button" onClick={() => startEdit(r)} style={{ background: 'transparent', border: '1px solid var(--ad-acc)', color: 'var(--ad-ink)', padding: '2px 8px', borderRadius: 4, cursor: 'pointer', fontSize: '0.74rem', marginRight: 4 }}>{t('Uredi', 'Edit')}</button>
                                    <button type="button" onClick={() => void del(r.id, r.code)} style={{ background: 'transparent', border: '1px solid #b14040', color: '#b14040', padding: '2px 8px', borderRadius: 4, cursor: 'pointer', fontSize: '0.74rem' }}>✕</button>
                                </td>
                            ) : null}
                        </tr>
                    ))}
                </tbody>
            </table>

            {draft ? (
                <div onClick={() => setDraft(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 80, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div onClick={(e) => e.stopPropagation()} style={{ background: '#fffdf6', padding: 18, borderRadius: 8, width: 'min(440px, 96vw)', boxShadow: '0 8px 30px rgba(0,0,0,0.25)' }}>
                        <h4 style={{ margin: 0, marginBottom: 10, color: 'var(--ad-ink)' }}>{editId ? t('Uredi način isporuke', 'Edit delivery method') : t('Nova metoda isporuke', 'New delivery method')}</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8, fontSize: '0.84rem' }}>
                            <label>{t('Šifra', 'Code')}</label>
                            <input type="text" disabled={!!editId} value={String(draft.code ?? '')}
                                onChange={(e) => setDraft({ ...draft, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                                placeholder="npr. hp_posta" style={{ padding: '4px 6px', fontFamily: 'ui-monospace, monospace' }} />
                            <label>{t('Naziv HR', 'Name HR')}</label>
                            <input type="text" value={draft.name_hr ?? ''} onChange={(e) => setDraft({ ...draft, name_hr: e.target.value })} style={{ padding: '4px 6px' }} />
                            <label>{t('Naziv EN', 'Name EN')}</label>
                            <input type="text" value={draft.name_en ?? ''} onChange={(e) => setDraft({ ...draft, name_en: e.target.value })} style={{ padding: '4px 6px' }} />
                            <label>{t('Max € / pošiljka', 'Max € / shipment')}</label>
                            <input type="number" min="0" step="100" value={draft.max_amount_eur == null ? '' : String(draft.max_amount_eur)}
                                onChange={(e) => setDraft({ ...draft, max_amount_eur: e.target.value === '' ? null : Number(e.target.value) })}
                                placeholder={t('prazno = bez limita', 'empty = no limit')} style={{ padding: '4px 6px' }} />
                            <label>{t('Aktivno', 'Active')}</label>
                            <input type="checkbox" checked={draft.is_active !== false} onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} style={{ justifySelf: 'start' }} />
                            <label>{t('Redoslijed', 'Order')}</label>
                            <input type="number" min="0" step="10" value={String(draft.sort_order ?? 999)} onChange={(e) => setDraft({ ...draft, sort_order: Number(e.target.value) })} style={{ padding: '4px 6px', width: 80 }} />
                            <label>{t('Bilješka', 'Note')}</label>
                            <textarea rows={2} value={draft.note ?? ''} onChange={(e) => setDraft({ ...draft, note: e.target.value })} style={{ padding: '4px 6px' }} />
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                            <button type="button" onClick={() => setDraft(null)} style={{ background: 'transparent', border: '1px solid #999', color: '#666', padding: '5px 12px', borderRadius: 4, cursor: 'pointer' }}>{t('Odustani', 'Cancel')}</button>
                            <button type="button" onClick={() => void save()} style={{ background: '#1f7a36', color: '#fff', border: 0, padding: '5px 12px', borderRadius: 4, cursor: 'pointer', fontWeight: 700 }}>{t('Spremi', 'Save')}</button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
